// 화면 18 — 종목별 밴드 이동 타임라인 드로어 (P1, O-DB-10).
// 권위: ARCH_DASHBOARD v1.10.3 §4.18 / §6.2, QUANT_SPEC §2.6.4.1.
//
// v1.10.3:
//   - 1차 소스 변경: leader_stocks_daily (sparse) → stock_daily_score (dense).
//   - rows 는 days 영업일 전체 — 진입 / 미진입 일자 모두 포함.
//   - in_top45 = true 인 일자만 별도 마커로 강조하여 "언제부터 Top45 에 들어왔는지" 한눈에.
//
// 차트: recharts 미도입 — SVG 자체 구현.
//   - 상단 strip: 일자별 band 색상 (전체). Top45 미진입 일자는 hatch overlay + 낮은 opacity 로 시각 구분.
//   - 하단 line: dense final_score 시계열 (NaN gap 자연 처리).
//   - markers: in_top45 = true 인 일자에만 큰 dot (r=4, stroke=accent) 강조.

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useBandHistory, useStockOhlcv } from './useLeadingStocks';
import {
  bandColor,
  bandSoftBg,
  DAYS_CAP,
  ohlcvStepLabel,
  OHLCV_DEFAULT_IDX,
  OHLCV_STEPS,
  type BandHistoryRow,
  type OhlcvInterval,
} from './types';
import { CandleChart } from './CandleChart';

interface Props {
  open: boolean;
  symbol: string | null;
  onClose: () => void;
}

const DAYS = 60;
const VIEW_W = 720;
const VIEW_H = 240;
const PAD = { top: 12, right: 16, bottom: 36, left: 44 };
const STRIP_H = 22;
const HATCH_ID = 'lst-band-hatch';

export const BandHistoryDrawer = memo(function BandHistoryDrawer({
  open,
  symbol,
  onClose,
}: Props): JSX.Element | null {
  const query = useBandHistory(open ? symbol : null, DAYS);

  // 캔들 차트 컨트롤 — drawer 내부 state (URL 동기화 없음).
  // 변수명 `interval` 은 글로벌 setInterval 과 헷갈리기 쉬워 ohlcvInterval 로 명명.
  const [ohlcvInterval, setOhlcvInterval] = useState<OhlcvInterval>('daily');
  const [stepIdx, setStepIdx] = useState<number>(OHLCV_DEFAULT_IDX);
  const ohlcvDays =
    OHLCV_STEPS[ohlcvInterval][stepIdx] ?? OHLCV_STEPS[ohlcvInterval][OHLCV_DEFAULT_IDX];

  // 옵션 C — 좌측 viewport 잠금 + lazy-load 확장.
  //   - effectiveDays: 실제 fetch days. chip 변경 시 ohlcvDays 로 리셋, lazy-load 시 1.5× 확장 (cap 까지).
  //   - resetViewport: CandleChart 의 scroll behavior 차등 제어 boolean.
  //       chip/interval 변경 → true (우측 정렬 복귀), lazy-load 결과 도착 → false (viewport 유지).
  const [effectiveDays, setEffectiveDays] = useState<number>(ohlcvDays);
  const [resetViewport, setResetViewport] = useState<boolean>(true);
  // 마운트 직후 chip/interval 동기화 effect 와 초기값 effect 가 중복 호출되는 것을 막기 위한 가드.
  // (state 초기값이 이미 ohlcvDays 이므로 첫 effect 발화 시점에 setState 는 no-op 이지만 ref 갱신은 필요 없음.)
  const lastSyncedRef = useRef<{ interval: OhlcvInterval; days: number }>({
    interval: ohlcvInterval,
    days: ohlcvDays,
  });

  // chip / interval 변경 시: effectiveDays = ohlcvDays, resetViewport = true.
  // (lazy-load 만으로는 setEffectiveDays(>ohlcvDays) 하므로 chip 변경을 명시적으로 감지 가능.)
  useEffect(() => {
    const prev = lastSyncedRef.current;
    if (prev.interval === ohlcvInterval && prev.days === ohlcvDays) return;
    lastSyncedRef.current = { interval: ohlcvInterval, days: ohlcvDays };
    setEffectiveDays(ohlcvDays);
    setResetViewport(true);
  }, [ohlcvInterval, ohlcvDays]);

  const ohlcv = useStockOhlcv(symbol, ohlcvInterval, effectiveDays, open);

  // 좌측 viewport 근접 시 CandleChart 가 호출. cap 비교 후 effectiveDays 확장.
  // currentEffectiveDays 인자는 chart 가 마지막으로 본 값 — race 회피용으로 받지만
  //   여기서는 state 최신값을 기준으로 cap 비교 (functional update 사용).
  const onRequestMore = useCallback(
    (_currentEffectiveDays: number): void => {
      const cap = DAYS_CAP[ohlcvInterval];
      setEffectiveDays((prev) => {
        if (prev >= cap) return prev;
        const next = Math.min(Math.ceil(prev * 1.5), cap);
        if (next <= prev) return prev;
        // lazy-load 결과 도착 시 viewport 유지 — CandleChart 가 scrollToPosition 호출 X.
        setResetViewport(false);
        return next;
      });
    },
    [ohlcvInterval]
  );

  // interval 전환 시 step idx 는 유지 (0~4 동일 인덱스 매핑).
  const onIntervalChange = (next: OhlcvInterval): void => {
    if (next === ohlcvInterval) return;
    setOhlcvInterval(next);
    // idx 가 그대로면 OHLCV_STEPS[next][stepIdx] 가 자동으로 동등 기간이 됨.
    // effectiveDays / resetViewport 동기화는 위 useEffect 가 담당.
  };

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !symbol) return null;

  const data = query.data;
  const inTop45Count = data?.rows.filter((r) => r.in_top45).length ?? 0;

  return createPortal(
    <div className="lst-drawer__backdrop" onClick={onClose} data-testid="lst-drawer-backdrop">
      <aside
        className="lst-drawer"
        role="dialog"
        aria-label={`${symbol} 밴드 이동 타임라인`}
        data-testid="lst-band-drawer"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="lst-drawer__header">
          <h3 className="lst-drawer__title">
            {data?.name ?? symbol}
            <span className="lst-muted" style={{ marginLeft: 8, fontWeight: 400 }}>
              {symbol}
            </span>
          </h3>
          <span className="lst-drawer__sub">
            {DAYS}거래일 · {data ? `${inTop45Count}건 Top45 진입 / ${data.rows.length} row` : '…'}
          </span>
          <button
            type="button"
            className="lst-drawer__close"
            onClick={onClose}
            aria-label="드로어 닫기"
          >
            ×
          </button>
        </header>
        <div className="lst-drawer__body">
          {/* ── 캔들 차트 섹션 (상단) ────────────────────── */}
          <section className="lst-candle-section" aria-label="캔들 차트">
            <div className="lst-candle-controls">
              <div
                className="lst-candle-controls__seg"
                role="tablist"
                aria-label="봉 주기"
              >
                {(['daily', 'weekly', 'monthly'] as const).map((iv) => (
                  <button
                    key={iv}
                    type="button"
                    role="tab"
                    aria-selected={ohlcvInterval === iv}
                    className={`lst-candle-controls__seg-btn${
                      ohlcvInterval === iv ? ' lst-candle-controls__seg-btn--active' : ''
                    }`}
                    onClick={() => onIntervalChange(iv)}
                  >
                    {iv === 'daily' ? '일봉' : iv === 'weekly' ? '주봉' : '월봉'}
                  </button>
                ))}
              </div>
              <div
                className="lst-candle-controls__steps"
                role="tablist"
                aria-label="기간"
              >
                {OHLCV_STEPS[ohlcvInterval].map((v, i) => (
                  <button
                    key={`${ohlcvInterval}-${v}`}
                    type="button"
                    role="tab"
                    aria-selected={stepIdx === i}
                    className={`lst-candle-controls__step-btn${
                      stepIdx === i ? ' lst-candle-controls__step-btn--active' : ''
                    }`}
                    onClick={() => setStepIdx(i)}
                  >
                    {ohlcvStepLabel(ohlcvInterval, v)}
                  </button>
                ))}
              </div>
            </div>

            {ohlcv.isError && (
              <div className="lst-banner lst-banner--error">
                캔들 로드 실패: {ohlcv.error?.message ?? 'unknown'}
              </div>
            )}
            {ohlcv.isLoading && !ohlcv.data && (
              <div className="lst-empty">캔들 불러오는 중…</div>
            )}
            {ohlcv.data && ohlcv.data.rows.length > 0 && (
              <CandleChart
                rows={ohlcv.data.rows}
                interval={ohlcvInterval}
                effectiveDays={effectiveDays}
                resetViewport={resetViewport}
                onRequestMore={onRequestMore}
              />
            )}
            {ohlcv.data && ohlcv.data.rows.length === 0 && (
              <div className="lst-empty">해당 기간 OHLCV 데이터 없음.</div>
            )}
          </section>

          {/* ── 60일 band timeline (기존) ───────────────── */}
          {query.isError && (
            <div className="lst-banner lst-banner--error">
              로드 실패: {query.error?.message ?? 'unknown'}
            </div>
          )}
          {query.isLoading && !data && <div className="lst-empty">불러오는 중…</div>}
          {data && data.rows.length > 0 && (
            <>
              <BandChart rows={data.rows} />
              <BandChartLegend />
              <BandHistoryTable rows={data.rows} />
            </>
          )}
          {data && data.rows.length === 0 && (
            <div className="lst-empty">
              최근 {DAYS}거래일 stock_daily_score row 없음.
            </div>
          )}
        </div>
      </aside>
    </div>,
    document.body
  );
});

interface BandChartProps {
  rows: BandHistoryRow[];
}

function BandChart({ rows }: BandChartProps): JSX.Element {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.trading_date.localeCompare(b.trading_date)),
    [rows]
  );

  const { dates, scores, yMin, yMax } = useMemo(() => {
    const ds: string[] = [];
    const ss: number[] = [];
    let mn = Infinity;
    let mx = -Infinity;
    for (const r of sorted) {
      ds.push(r.trading_date);
      if (r.final_score != null) {
        ss.push(r.final_score);
        if (r.final_score < mn) mn = r.final_score;
        if (r.final_score > mx) mx = r.final_score;
      } else {
        ss.push(NaN);
      }
    }
    if (!Number.isFinite(mn)) {
      mn = 0;
      mx = 1;
    } else if (mn === mx) {
      mn -= 1;
      mx += 1;
    }
    return { dates: ds, scores: ss, yMin: mn, yMax: mx };
  }, [sorted]);

  const plotW = VIEW_W - PAD.left - PAD.right;
  const plotH = VIEW_H - PAD.top - PAD.bottom - STRIP_H - 8;
  const stripY = PAD.top;
  const lineTop = PAD.top + STRIP_H + 8;

  const xOf = (i: number): number => {
    if (dates.length === 0) return PAD.left;
    if (dates.length === 1) return PAD.left + plotW / 2;
    return PAD.left + (i / (dates.length - 1)) * plotW;
  };

  const yOf = (s: number): number => {
    return lineTop + (1 - (s - yMin) / (yMax - yMin)) * plotH;
  };

  // 라인 path (NaN gap 자연 처리) — dense 시계열 전체.
  const linePath = useMemo(() => {
    const parts: string[] = [];
    let pendingMove = true;
    for (let i = 0; i < scores.length; i += 1) {
      const s = scores[i];
      if (Number.isNaN(s)) {
        pendingMove = true;
        continue;
      }
      const x = xOf(i);
      const y = yOf(s);
      if (pendingMove) {
        parts.push(`M ${x.toFixed(2)} ${y.toFixed(2)}`);
        pendingMove = false;
      } else {
        parts.push(`L ${x.toFixed(2)} ${y.toFixed(2)}`);
      }
    }
    return parts.join(' ');
  }, [scores, dates.length, yMin, yMax]);

  // 밴드 strip — 각 일자 한 칸.
  const stripWidth = dates.length > 1 ? plotW / (dates.length - 1) : plotW;
  const halfW = stripWidth / 2;

  // Y축 tick 5개.
  const yTicks = useMemo(() => {
    const n = 4;
    const arr: number[] = [];
    for (let i = 0; i <= n; i += 1) {
      arr.push(yMin + ((yMax - yMin) * i) / n);
    }
    return arr;
  }, [yMin, yMax]);

  // X축 tick — 시작/중간/끝 3개.
  const xTickIdx = useMemo(() => {
    if (dates.length === 0) return [] as number[];
    if (dates.length === 1) return [0];
    return [0, Math.floor(dates.length / 2), dates.length - 1];
  }, [dates.length]);

  return (
    <svg
      className="lst-band-chart"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="밴드 이동 타임라인 (dense + Top45 진입 마커)"
    >
      {/* hatch pattern — Top45 미진입 일자 strip overlay 용 */}
      <defs>
        <pattern
          id={HATCH_ID}
          patternUnits="userSpaceOnUse"
          width={6}
          height={6}
          patternTransform="rotate(45)"
        >
          <line x1={0} y1={0} x2={0} y2={6} stroke="var(--ink-faint)" strokeWidth={1} opacity={0.5} />
        </pattern>
      </defs>

      {/* 가로 grid */}
      {yTicks.map((t) => {
        const y = yOf(t);
        return (
          <line
            key={`grid-${t}`}
            className="lst-band-chart__grid"
            x1={PAD.left}
            x2={VIEW_W - PAD.right}
            y1={y}
            y2={y}
          />
        );
      })}
      {/* Y tick label */}
      {yTicks.map((t) => (
        <text
          key={`yt-${t}`}
          className="lst-band-chart__tick"
          x={PAD.left - 6}
          y={yOf(t) + 3}
          textAnchor="end"
        >
          {t.toFixed(1)}
        </text>
      ))}
      {/* X tick label */}
      {xTickIdx.map((i) => (
        <text
          key={`xt-${i}`}
          className="lst-band-chart__tick"
          x={xOf(i)}
          y={VIEW_H - PAD.bottom + 14}
          textAnchor="middle"
        >
          {dates[i]?.slice(5)}
        </text>
      ))}

      {/* 밴드 strip (1단) — band 색상 (Top45 미진입은 opacity 낮춤) */}
      {sorted.map((r, i) => {
        const cx = xOf(i);
        return (
          <rect
            key={`strip-${r.trading_date}`}
            className={`lst-band-chart__band-strip${
              r.in_top45 ? ' lst-band-chart__band-strip--top45' : ''
            }`}
            x={cx - halfW}
            y={stripY}
            width={Math.max(stripWidth, 1.5)}
            height={STRIP_H}
            fill={bandColor(r.band)}
          >
            <title>
              {r.trading_date} · band {r.band ?? 'unclassified'}
              {r.in_top45
                ? ` · Top45 진입 · rank ${r.rank_in_sector} · ${r.sector ?? '-'}`
                : ' · Top45 미진입'}
              {r.final_score != null ? ` · score ${r.final_score.toFixed(2)}` : ''}
            </title>
          </rect>
        );
      })}

      {/* 밴드 strip (2단 overlay) — Top45 미진입 일자에 hatch */}
      {sorted.map((r, i) => {
        if (r.in_top45) return null;
        const cx = xOf(i);
        return (
          <rect
            key={`hatch-${r.trading_date}`}
            x={cx - halfW}
            y={stripY}
            width={Math.max(stripWidth, 1.5)}
            height={STRIP_H}
            fill={`url(#${HATCH_ID})`}
            pointerEvents="none"
          />
        );
      })}

      {/* 축 */}
      <line
        className="lst-band-chart__axis"
        x1={PAD.left}
        x2={PAD.left}
        y1={lineTop}
        y2={lineTop + plotH}
      />
      <line
        className="lst-band-chart__axis"
        x1={PAD.left}
        x2={VIEW_W - PAD.right}
        y1={lineTop + plotH}
        y2={lineTop + plotH}
      />

      {/* dense score line — 전체 일자 */}
      {linePath && <path className="lst-band-chart__line" d={linePath} />}

      {/* dense baseline marker — 모든 score 일자에 작은 점 (Top45 진입 여부 무관) */}
      {sorted.map((r, i) => {
        if (r.final_score == null) return null;
        if (r.in_top45) return null; // Top45 마커가 따로 그려짐.
        return (
          <circle
            key={`bm-${r.trading_date}`}
            className="lst-band-chart__marker"
            cx={xOf(i)}
            cy={yOf(r.final_score)}
            r={1.6}
            fill={bandColor(r.band)}
            opacity={0.45}
          />
        );
      })}

      {/* Top45 진입 마커 — 큰 dot + 강조 stroke */}
      {sorted.map((r, i) => {
        if (!r.in_top45) return null;
        if (r.final_score == null) return null;
        return (
          <circle
            key={`top45-${r.trading_date}`}
            className="lst-band-chart__marker lst-band-chart__marker--top45"
            cx={xOf(i)}
            cy={yOf(r.final_score)}
            r={4}
            fill={bandColor(r.band)}
            stroke={bandSoftBg(r.band)}
            strokeWidth={2}
          >
            <title>
              {r.trading_date} · Top45 진입 · rank {r.rank_in_sector} · {r.sector ?? '-'} ·
              band {r.band ?? '-'} · score{' '}
              {r.final_score != null ? r.final_score.toFixed(2) : '-'}
            </title>
          </circle>
        );
      })}
    </svg>
  );
}

function BandChartLegend(): JSX.Element {
  return (
    <div className="lst-band-chart__legend" aria-label="차트 범례">
      <span className="lst-band-chart__legend-item">
        <span
          className="lst-band-chart__legend-swatch lst-band-chart__legend-swatch--top45"
          aria-hidden="true"
        />
        Top45 진입 일자 (큰 마커, 색상 강조)
      </span>
      <span className="lst-band-chart__legend-item">
        <span
          className="lst-band-chart__legend-swatch lst-band-chart__legend-swatch--hatch"
          aria-hidden="true"
        />
        Top45 미진입 일자 (빗금 strip, 작은 점)
      </span>
    </div>
  );
}

interface BandHistoryTableProps {
  rows: BandHistoryRow[];
}

function BandHistoryTable({ rows }: BandHistoryTableProps): JSX.Element {
  // DESC 표시 (최신 우선).
  const sorted = useMemo(
    () => [...rows].sort((a, b) => b.trading_date.localeCompare(a.trading_date)),
    [rows]
  );
  return (
    <table className="lst-drawer__table">
      <thead>
        <tr>
          <th>date</th>
          <th>top45</th>
          <th>band</th>
          <th>rank</th>
          <th>sector</th>
          <th style={{ textAlign: 'right' }}>final_score</th>
        </tr>
      </thead>
      <tbody>
        {sorted.map((r) => (
          <tr
            key={r.trading_date}
            className={r.in_top45 ? 'lst-drawer__table-row--top45' : undefined}
          >
            <td>{r.trading_date}</td>
            <td>{r.in_top45 ? '●' : '—'}</td>
            <td>
              <span
                className="lst-diff__entry-band"
                style={{ background: bandColor(r.band) }}
                aria-label={`band ${r.band ?? 'unclassified'}`}
              >
                {r.band ?? '—'}
              </span>
            </td>
            <td>{r.rank_in_sector ?? '—'}</td>
            <td>{r.sector ?? '—'}</td>
            <td style={{ textAlign: 'right' }}>
              {r.final_score != null ? r.final_score.toFixed(2) : '—'}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
